import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Trash2, Save, ArrowLeft, X, FileText as FileTextIcon } from 'lucide-react';

export default function AddBill() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { billId } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = !!billId;
  const fromBooking = searchParams.get('from_booking');
  const isManagerFromBooking = fromBooking && (user?.role === 'manager' || user?.role === 'regional_manager');

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  
  // Master data
  const [branches, setBranches] = useState([]);
  const [theaters, setTheaters] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [cakes, setCakes] = useState([]);
  const [decorations, setDecorations] = useState([]);
  const [foodItems, setFoodItems] = useState([]);
  const [scanners, setScanners] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    branch_id: user?.branch_id || '',
    theater_id: '',
    time_slot: '',
    num_people: 2,
    discount_amount: 0,
    discount_reason: '',
    advance_paid: 0,
    payment_mode: 'cash',
    cash_amount: 0,
    online_amount: 0,
    scanner_id: '',
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    billed_by: '',
    is_cancelled: false,
    cancellation_reason: ''
  });

  const [selectedTheater, setSelectedTheater] = useState(null);
  const [selectedItems, setSelectedItems] = useState({
    cakes: [],
    decorations: [],
    food: [],
    miscellaneous: []
  });

  const [miscForm, setMiscForm] = useState({
    description: '',
    quantity: 1,
    price: 0
  });

  useEffect(() => {
    fetchMasterData();
  }, []);

  useEffect(() => {
    if (billId && branches.length > 0) {
      fetchBillData();
    }
  }, [billId, branches]);

  useEffect(() => {
    if (formData.branch_id) {
      fetchBranchData(formData.branch_id);
      fetchScanners(formData.branch_id);
    }
  }, [formData.branch_id]);

  useEffect(() => {
    if (formData.theater_id) {
      const theater = theaters.find(t => t.id === formData.theater_id);
      setSelectedTheater(theater);
      fetchTheaterSlots(theater?.name);
    }
  }, [formData.theater_id, theaters]);

  // Pre-fill form from website booking query params
  useEffect(() => {
    if (fromBooking && branches.length > 0 && !isEdit) {
      const branchId = searchParams.get('branch_id');
      const updates = {};
      if (searchParams.get('date')) updates.date = searchParams.get('date');
      if (branchId) updates.branch_id = branchId;
      if (searchParams.get('customer_name')) updates.customer_name = searchParams.get('customer_name');
      if (searchParams.get('customer_phone')) updates.customer_phone = searchParams.get('customer_phone');
      const advPaid = parseFloat(searchParams.get('advance_paid') || '0');
      if (advPaid > 0) updates.advance_paid = advPaid;
      if (Object.keys(updates).length > 0) {
        setFormData(prev => ({ ...prev, ...updates }));
      }
    }
  }, [fromBooking, branches, isEdit, searchParams]);

  // After theaters load, match the theater name from booking
  useEffect(() => {
    if (fromBooking && theaters.length > 0 && !isEdit && !formData.theater_id) {
      const theaterName = searchParams.get('theater');
      if (theaterName) {
        const matched = theaters.find(t => t.name.toLowerCase() === theaterName.toLowerCase());
        if (matched) {
          setFormData(prev => ({ ...prev, theater_id: matched.id }));
        }
      }
    }
  }, [fromBooking, theaters, isEdit, searchParams, formData.theater_id]);

  // After time slots load, match the time slot from booking
  useEffect(() => {
    if (fromBooking && timeSlots.length > 0 && !isEdit && !formData.time_slot) {
      const bookingSlot = searchParams.get('time_slot');
      if (bookingSlot) {
        // Normalize: remove spaces around dash for comparison
        const normalize = (s) => (s || '').replace(/\s*-\s*/g, '-').trim();
        const matched = timeSlots.find(ts => normalize(ts.time_slot) === normalize(bookingSlot));
        if (matched) {
          setFormData(prev => ({ ...prev, time_slot: matched.time_slot }));
        }
      }
    }
  }, [fromBooking, timeSlots, isEdit, searchParams, formData.time_slot]);

  const fetchMasterData = async () => {
    try {
      const branchesRes = await api.get('/branches');
      setBranches(branchesRes.data);
    } catch (error) {
      toast.error('Failed to fetch master data');
    }
  };

  const fetchScanners = async (branchId) => {
    try {
      if (branchId) {
        const scannersRes = await api.get(`/scanners?branch_id=${branchId}`);
        setScanners(scannersRes.data);
      } else {
        setScanners([]);
      }
    } catch (error) {
      toast.error('Failed to fetch scanners');
    }
  };

  const fetchBillData = async () => {
    setLoadingData(true);
    try {
      const response = await api.get(`/bills/${billId}`);
      const bill = response.data;
      if (bill) {
        setFormData({
          date: bill.date,
          branch_id: bill.branch_id,
          theater_id: bill.theater_id,
          time_slot: bill.time_slot,
          num_people: bill.num_people,
          discount_amount: bill.discount_amount,
          discount_reason: bill.discount_reason || '',
          advance_paid: bill.advance_paid || 0,
          payment_mode: bill.payment_mode,
          cash_amount: bill.cash_amount || 0,
          online_amount: bill.online_amount || 0,
          scanner_id: bill.scanner_id || '',
          customer_name: bill.customer_name || '',
          customer_phone: bill.customer_phone || '',
          customer_email: bill.customer_email || '',
          billed_by: bill.billed_by || '',
          is_cancelled: bill.is_cancelled || false,
          cancellation_reason: bill.cancellation_reason || ''
        });

        // Reconstruct selected items
        const cakeItems = bill.items.filter(i => i.item_type === 'cake');
        const decorationItems = bill.items.filter(i => i.item_type === 'decoration');
        const foodItemsList = bill.items.filter(i => i.item_type === 'food');
        const miscItems = bill.items.filter(i => i.item_type === 'miscellaneous');

        setSelectedItems({
          cakes: cakeItems.map(i => ({ id: i.item_id, name: i.item_name, price: i.price, quantity: i.quantity })),
          decorations: decorationItems.map(i => ({ id: i.item_id, name: i.item_name, price: i.price, quantity: i.quantity })),
          food: foodItemsList.map(i => ({ id: i.item_id, name: i.item_name, price: i.price, quantity: i.quantity })),
          miscellaneous: miscItems.map(i => ({ id: i.item_id, name: i.item_name, price: i.price, quantity: i.quantity }))
        });
      }
    } catch (error) {
      toast.error('Failed to fetch bill data');
    } finally {
      setLoadingData(false);
    }
  };

  const fetchBranchData = async (branchId) => {
    try {
      const [theatersRes, cakesRes, decorationsRes, foodRes] = await Promise.all([
        api.get(`/theaters?branch_id=${branchId}`),
        api.get(`/cakes?branch_id=${branchId}`),
        api.get(`/decorations?branch_id=${branchId}`),
        api.get(`/food-beverages?branch_id=${branchId}`)
      ]);
      setTheaters(theatersRes.data);
      // Sort alphabetically by name
      setCakes(cakesRes.data.sort((a, b) => a.name.localeCompare(b.name)));
      setDecorations(decorationsRes.data.sort((a, b) => a.name.localeCompare(b.name)));
      setFoodItems(foodRes.data.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      toast.error('Failed to fetch branch data');
    }
  };

  const fetchTheaterSlots = async (theaterName) => {
    try {
      const response = await api.get(`/theater-slots/${theaterName}`);
      setTimeSlots(response.data);
    } catch (error) {
      toast.error('Failed to fetch time slots');
    }
  };

  const addItem = (type, item) => {
    const existing = selectedItems[type].find(i => i.id === item.id);
    if (existing) {
      toast.warning('Item already added');
      return;
    }
    setSelectedItems({
      ...selectedItems,
      [type]: [...selectedItems[type], { ...item, quantity: 1 }]
    });
  };

  const removeItem = (type, itemId) => {
    setSelectedItems({
      ...selectedItems,
      [type]: selectedItems[type].filter(i => i.id !== itemId)
    });
  };

  const updateItemQuantity = (type, itemId, quantity) => {
    setSelectedItems({
      ...selectedItems,
      [type]: selectedItems[type].map(i => 
        i.id === itemId ? { ...i, quantity: Math.max(1, parseInt(quantity) || 1) } : i
      )
    });
  };

  const addMiscellaneousItem = () => {
    if (!miscForm.description || miscForm.price <= 0) {
      toast.error('Please enter description and price for miscellaneous item');
      return;
    }
    
    const newItem = {
      id: crypto.randomUUID(),
      name: miscForm.description,
      quantity: miscForm.quantity,
      price: miscForm.price
    };
    
    setSelectedItems({
      ...selectedItems,
      miscellaneous: [...selectedItems.miscellaneous, newItem]
    });
    
    setMiscForm({ description: '', quantity: 1, price: 0 });
    toast.success('Miscellaneous item added');
  };

  const calculateTheaterPrice = () => {
    if (!selectedTheater) return 0;
    const extraPeople = Math.max(0, formData.num_people - selectedTheater.min_capacity);
    return selectedTheater.price + (extraPeople * selectedTheater.extra_person_charge);
  };

  const calculateItemsTotal = () => {
    const cakesTotal = selectedItems.cakes.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const decorationsTotal = selectedItems.decorations.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const foodTotal = selectedItems.food.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const miscTotal = selectedItems.miscellaneous.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    return cakesTotal + decorationsTotal + foodTotal + miscTotal;
  };

  const calculateTotalBill = () => {
    return calculateTheaterPrice() + calculateItemsTotal();
  };

  const calculateActualAmount = () => {
    return calculateTotalBill() - formData.discount_amount;
  };

  const calculateBalanceDue = () => {
    return calculateActualAmount() - formData.advance_paid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validations for all bills
    if (!formData.branch_id || !formData.theater_id || !formData.time_slot) {
      toast.error('Please fill all required fields');
      return;
    }

    // Customer details validation (required for all bills)
    if (!formData.customer_name || !formData.customer_phone) {
      toast.error('Customer name and phone number are required');
      return;
    }

    if (formData.customer_phone.length !== 10) {
      toast.error('Phone number must be exactly 10 digits');
      return;
    }

    // Email validation (if provided)
    if (formData.customer_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.customer_email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Cancelled bill validation (simplified)
    if (formData.is_cancelled) {
      if (!formData.cancellation_reason.trim()) {
        toast.error('Please provide a reason for cancellation');
        return;
      }
      // For cancelled bills, only basic info is needed - skip other validations
    } else {
      // Regular bill validations (only for non-cancelled bills)
      if (formData.discount_amount > 0 && !formData.discount_reason) {
        toast.error('Please provide a reason for the discount');
        return;
      }

      if (formData.payment_mode === 'online' && !formData.scanner_id) {
        toast.error('Please select a scanner for online payment');
        return;
      }

      if (formData.payment_mode === 'both') {
        if (!formData.cash_amount || !formData.online_amount || !formData.scanner_id) {
          toast.error('Please provide cash and online amounts with scanner');
          return;
        }
      }
    }

    setLoading(true);

    try {
      let payload;
      
      if (formData.is_cancelled) {
        // Simplified payload for cancelled bills - only essential data
        payload = {
          date: formData.date,
          branch_id: formData.branch_id,
          theater_id: formData.theater_id,
          time_slot: formData.time_slot,
          num_people: 1, // Default for cancelled
          items: [], // No items for cancelled bills
          total_theater_price: 0,
          total_bill_amount: 0,
          discount_amount: 0,
          actual_amount: 0,
          advance_paid: parseFloat(formData.advance_paid || 0),
          balance_due: 0,
          payment_mode: 'cash', // Default for cancelled
          cash_amount: 0,
          online_amount: null,
          scanner_id: null,
          customer_name: formData.customer_name,
          customer_phone: formData.customer_phone,
          customer_email: formData.customer_email || null,
          billed_by: formData.billed_by || null,
          is_cancelled: true,
          cancellation_reason: formData.cancellation_reason
        };
      } else {
        // Full payload for regular bills
        const items = [
          ...selectedItems.cakes.map(i => ({
            item_type: 'cake',
            item_id: i.id,
            item_name: i.name,
            quantity: i.quantity,
            price: i.price,
            total: i.price * i.quantity
          })),
          ...selectedItems.decorations.map(i => ({
            item_type: 'decoration',
            item_id: i.id,
            item_name: i.name,
            quantity: i.quantity,
            price: i.price,
            total: i.price * i.quantity
          })),
          ...selectedItems.food.map(i => ({
            item_type: 'food',
            item_id: i.id,
            item_name: i.name,
            quantity: i.quantity,
            price: i.price,
            total: i.price * i.quantity
          })),
          ...selectedItems.miscellaneous.map(i => ({
            item_type: 'miscellaneous',
            item_id: i.id,
            item_name: i.name,
            quantity: i.quantity,
            price: i.price,
            total: i.price * i.quantity
          }))
        ];

        payload = {
          ...formData,
          items,
          num_people: parseInt(formData.num_people),
          discount_amount: parseFloat(formData.discount_amount),
          advance_paid: parseFloat(formData.advance_paid || 0),
          cash_amount: (formData.payment_mode === 'cash' || formData.payment_mode === 'both') ? 
                       parseFloat(formData.cash_amount) : null,
          online_amount: (formData.payment_mode === 'online' || formData.payment_mode === 'both') ? 
                         parseFloat(formData.online_amount) : null,
          scanner_id: formData.payment_mode !== 'cash' ? formData.scanner_id : null,
          is_cancelled: false
        };
      }

      if (isEdit) {
        await api.put(`/bills/${billId}`, payload);
        toast.success('Bill updated successfully!');
      } else {
        await api.post('/bills', payload);
        toast.success(formData.is_cancelled ? 
          'Cancelled bill recorded successfully' : 
          'Bill created successfully!'
        );
      }

      // If adding from a website booking, rematch to update status
      if (fromBooking) {
        try {
          await api.post(`/website-bookings/${fromBooking}/rematch`);
        } catch { /* ignore rematch errors */ }
        navigate('/website-bookings');
      } else {
        navigate('/view-bills');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save bill');
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading bill data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-4">
        <Button
          data-testid="back-btn"
          onClick={() => navigate(fromBooking ? '/website-bookings' : '/view-bills')}
          variant="ghost"
          size="sm"
          className="gap-2"
        >
          <ArrowLeft size={16} />
          Back
        </Button>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Space Grotesk' }}>
          {isEdit ? 'Edit Bill' : 'Add New Bill'}
        </h1>
      </div>

      {/* Cancelled Bill Warning - Show in edit mode for cancelled bills */}
      {isEdit && formData.is_cancelled && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-800">
            <X size={20} />
            <h3 className="font-semibold">Cancelled Bill</h3>
          </div>
          <p className="text-red-700 mt-2">
            <strong>Reason:</strong> {formData.cancellation_reason || 'No reason provided'}
          </p>
          <p className="text-xs text-red-600 mt-2">
            This bill was marked as cancelled. Edit is not recommended for cancelled bills.
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Website Booking Banner */}
        {fromBooking && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4" data-testid="booking-banner">
            <div className="flex items-center gap-2 text-indigo-800">
              <FileTextIcon size={20} />
              <h3 className="font-semibold">
                Adding bill for {searchParams.get('date') ? new Date(searchParams.get('date') + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}, {searchParams.get('theater') || ''}, {searchParams.get('time_slot') || ''}
              </h3>
            </div>
          </div>
        )}
        {/* Basic Details */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date">Date *</Label>
                <Input
                  id="date"
                  data-testid="bill-date-input"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  max={new Date().toISOString().split('T')[0]}
                  min={user?.role === 'admin' ? undefined : (user?.role === 'regional_manager' ? new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0] : new Date(Date.now() - 86400000).toISOString().split('T')[0])}
                  required
                  disabled={isManagerFromBooking}
                  className={isManagerFromBooking ? 'bg-slate-100 cursor-not-allowed' : ''}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="branch">Branch *</Label>
                <Select
                  value={formData.branch_id}
                  onValueChange={(value) => setFormData({ ...formData, branch_id: value, theater_id: '', time_slot: '' })}
                  disabled={user?.role !== 'admin'}
                >
                  <SelectTrigger data-testid="branch-select">
                    <SelectValue placeholder="Select branch" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map(branch => (
                      <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="theater">Theater *</Label>
                <Select
                  value={formData.theater_id}
                  onValueChange={(value) => setFormData({ ...formData, theater_id: value, time_slot: '' })}
                  disabled={!formData.branch_id || isManagerFromBooking}
                >
                  <SelectTrigger data-testid="theater-select">
                    <SelectValue placeholder="Select theater" />
                  </SelectTrigger>
                  <SelectContent>
                    {theaters.map(theater => (
                      <SelectItem key={theater.id} value={theater.id}>{theater.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="time_slot">Time Slot *</Label>
                <Select
                  value={formData.time_slot}
                  onValueChange={(value) => setFormData({ ...formData, time_slot: value })}
                  disabled={!formData.theater_id || isManagerFromBooking}
                >
                  <SelectTrigger data-testid="timeslot-select">
                    <SelectValue placeholder="Select time slot" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map(slot => (
                      <SelectItem key={slot.id} value={slot.time_slot}>{slot.time_slot}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="num_people">Number of People *</Label>
                <Input
                  id="num_people"
                  data-testid="num-people-input"
                  type="number"
                  min="1"
                  value={formData.num_people}
                  onChange={(e) => setFormData({ ...formData, num_people: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Theater Price</Label>
                <Input
                  data-testid="theater-price-display"
                  value={`₹${calculateTheaterPrice().toFixed(2)}`}
                  readOnly
                  className="bg-slate-50"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="advance_paid">Advance Paid</Label>
                <Input
                  id="advance_paid"
                  data-testid="advance-paid-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.advance_paid}
                  onChange={(e) => setFormData({ ...formData, advance_paid: e.target.value })}
                  placeholder="Enter advance amount"
                  disabled={isManagerFromBooking}
                  className={isManagerFromBooking ? 'bg-slate-100 cursor-not-allowed' : ''}
                />
              </div>

              {/* Customer Details */}
              <div className="space-y-2">
                <Label htmlFor="customer_name">Customer Name *</Label>
                <Input
                  id="customer_name"
                  data-testid="customer-name-input"
                  type="text"
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  placeholder="Enter customer name"
                  required
                  disabled={isManagerFromBooking}
                  className={isManagerFromBooking ? 'bg-slate-100 cursor-not-allowed' : ''}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="customer_phone">Customer Phone *</Label>
                <Input
                  id="customer_phone"
                  data-testid="customer-phone-input"
                  type="tel"
                  value={formData.customer_phone}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (value.length <= 10) {
                      setFormData({ ...formData, customer_phone: value });
                    }
                  }}
                  placeholder="10 digit phone number"
                  maxLength="10"
                  required
                  disabled={isManagerFromBooking}
                  className={isManagerFromBooking ? 'bg-slate-100 cursor-not-allowed' : ''}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="customer_email">Customer Email (Optional)</Label>
                <Input
                  id="customer_email"
                  data-testid="customer-email-input"
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                  placeholder="customer@example.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="billed_by">Billed By (Optional)</Label>
                <Input
                  id="billed_by"
                  data-testid="billed-by-input"
                  type="text"
                  value={formData.billed_by || ''}
                  onChange={(e) => setFormData({ ...formData, billed_by: e.target.value })}
                  placeholder="Enter name of person who handled billing"
                />
                <p className="text-xs text-slate-500">Record the person who handled the customer and billing</p>
              </div>

              {/* Cancellation Option - Read-only in edit mode */}
              <div className="space-y-4 p-4 border rounded-lg bg-red-50 border-red-200">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_cancelled"
                    checked={formData.is_cancelled}
                    disabled={isEdit} // Disable in edit mode
                    onCheckedChange={(checked) => {
                      if (!isEdit) { // Only allow changes when creating new bills
                        setFormData({ 
                          ...formData, 
                          is_cancelled: checked,
                          cancellation_reason: checked ? formData.cancellation_reason : ''
                        });
                      }
                    }}
                  />
                  <Label htmlFor="is_cancelled" className={`font-medium ${
                    formData.is_cancelled ? 'text-red-800' : 'text-red-700'
                  }`}>
                    Mark as cancelled bill {isEdit && formData.is_cancelled ? '(Already Cancelled)' : ''}
                  </Label>
                </div>
                
                {formData.is_cancelled && (
                  <div className="space-y-2">
                    <Label htmlFor="cancellation_reason">Cancellation Reason *</Label>
                    <Textarea
                      id="cancellation_reason"
                      placeholder="Enter reason for cancellation (e.g., customer didn't show up, cancelled booking, etc.)"
                      rows={3}
                      value={formData.cancellation_reason}
                      disabled={isEdit} // Disable in edit mode
                      onChange={(e) => {
                        if (!isEdit) { // Only allow changes when creating new bills
                          setFormData({ ...formData, cancellation_reason: e.target.value });
                        }
                      }}
                      className={`border-red-300 focus:border-red-500 ${
                        isEdit ? 'bg-gray-100 cursor-not-allowed' : ''
                      }`}
                      required={formData.is_cancelled && !isEdit}
                    />
                  </div>
                )}
                
                <p className="text-xs text-red-700">
                  <X size={12} className="inline mr-1" />
                  {isEdit && formData.is_cancelled ? 
                    'This bill was marked as cancelled during creation.' :
                    'Use this for customers who cancelled or didn\'t show up. Only customer details and advance amount will be recorded.'
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Items Selection - Hidden when bill is cancelled */}
        {!formData.is_cancelled && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Cakes */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Cakes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Select onValueChange={(value) => {
                const cake = cakes.find(c => c.id === value);
                if (cake) addItem('cakes', cake);
              }}>
                <SelectTrigger data-testid="cake-select">
                  <SelectValue placeholder="Add cake" />
                </SelectTrigger>
                <SelectContent>
                  {cakes.map(cake => (
                    <SelectItem key={cake.id} value={cake.id}>
                      {cake.name} - ₹{cake.price}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="space-y-2">
                {selectedItems.cakes.map(item => (
                  <div key={item.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                    <div className="flex-1">
                      <p className="text-sm font-medium">{item.name}</p>
                      <p className="text-xs text-slate-600">₹{item.price} × {item.quantity}</p>
                    </div>
                    <Input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => updateItemQuantity('cakes', item.id, e.target.value)}
                      className="w-16 h-8 text-sm"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeItem('cakes', item.id)}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Decorations */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Extra Decorations</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Select onValueChange={(value) => {
                const decoration = decorations.find(d => d.id === value);
                if (decoration) addItem('decorations', decoration);
              }}>
                <SelectTrigger data-testid="decoration-select">
                  <SelectValue placeholder="Add decoration" />
                </SelectTrigger>
                <SelectContent>
                  {decorations.map(decoration => (
                    <SelectItem key={decoration.id} value={decoration.id}>
                      {decoration.name} - ₹{decoration.price}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="space-y-2">
                {selectedItems.decorations.map(item => (
                  <div key={item.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                    <div className="flex-1">
                      <p className="text-sm font-medium">{item.name}</p>
                      <p className="text-xs text-slate-600">₹{item.price} × {item.quantity}</p>
                    </div>
                    <Input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => updateItemQuantity('decorations', item.id, e.target.value)}
                      className="w-16 h-8 text-sm"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeItem('decorations', item.id)}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Food & Beverages */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Food & Beverages</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Select onValueChange={(value) => {
                const food = foodItems.find(f => f.id === value);
                if (food) addItem('food', food);
              }}>
                <SelectTrigger data-testid="food-select">
                  <SelectValue placeholder="Add food/beverage" />
                </SelectTrigger>
                <SelectContent>
                  {foodItems.map(food => (
                    <SelectItem key={food.id} value={food.id}>
                      {food.name} - ₹{food.price}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="space-y-2">
                {selectedItems.food.map(item => (
                  <div key={item.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                    <div className="flex-1">
                      <p className="text-sm font-medium">{item.name}</p>
                      <p className="text-xs text-slate-600">₹{item.price} × {item.quantity}</p>
                    </div>
                    <Input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => updateItemQuantity('food', item.id, e.target.value)}
                      className="w-16 h-8 text-sm"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeItem('food', item.id)}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Miscellaneous Items */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Miscellaneous Items</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="md:col-span-2">
                  <Label htmlFor="misc_description">Description</Label>
                  <Input
                    id="misc_description"
                    value={miscForm.description}
                    onChange={(e) => setMiscForm({ ...miscForm, description: e.target.value })}
                    placeholder="e.g., Extra Balloons, Setup Charges"
                  />
                </div>
                <div>
                  <Label htmlFor="misc_quantity">Quantity</Label>
                  <Input
                    id="misc_quantity"
                    type="number"
                    min="1"
                    value={miscForm.quantity}
                    onChange={(e) => setMiscForm({ ...miscForm, quantity: parseInt(e.target.value) || 1 })}
                  />
                </div>
                <div>
                  <Label htmlFor="misc_price">Price</Label>
                  <Input
                    id="misc_price"
                    type="number"
                    min="0"
                    step="0.01"
                    value={miscForm.price}
                    onChange={(e) => setMiscForm({ ...miscForm, price: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <Button type="button" onClick={addMiscellaneousItem} variant="outline" className="w-full">
                <Plus size={16} className="mr-2" /> Add Miscellaneous Item
              </Button>

              <div className="space-y-2">
                {selectedItems.miscellaneous.map(item => (
                  <div key={item.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium">{item.name}</p>
                      <p className="text-sm text-slate-600">₹{item.price} × {item.quantity} = ₹{(item.price * item.quantity).toFixed(2)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateItemQuantity('miscellaneous', item.id, e.target.value)}
                        className="w-16"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeItem('miscellaneous', item.id)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
        )}

        {/* Payment Details - Hidden when bill is cancelled */}
        {!formData.is_cancelled && (
        <Card>
          <CardHeader>
            <CardTitle>Payment Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
              <div>
                <Label className="text-sm text-slate-600">Theater Price</Label>
                <p className="text-lg font-bold">₹{calculateTheaterPrice().toFixed(2)}</p>
              </div>
              <div>
                <Label className="text-sm text-slate-600">Items Total</Label>
                <p className="text-lg font-bold">₹{calculateItemsTotal().toFixed(2)}</p>
              </div>
              <div>
                <Label className="text-sm text-slate-600">Total Bill</Label>
                <p className="text-xl font-bold text-indigo-600" data-testid="total-bill-amount">₹{calculateTotalBill().toFixed(2)}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="discount_amount">Discount Amount</Label>
                <Input
                  id="discount_amount"
                  data-testid="discount-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.discount_amount}
                  onChange={(e) => setFormData({ ...formData, discount_amount: e.target.value })}
                />
              </div>

              {parseFloat(formData.discount_amount) > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="discount_reason">Discount Reason *</Label>
                  <Input
                    id="discount_reason"
                    data-testid="discount-reason-input"
                    value={formData.discount_reason}
                    onChange={(e) => setFormData({ ...formData, discount_reason: e.target.value })}
                    placeholder="Enter reason for discount"
                    required={parseFloat(formData.discount_amount) > 0}
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <Label className="text-sm text-green-800">Actual Amount</Label>
                <p className="text-2xl font-bold text-green-600" data-testid="actual-amount">₹{calculateActualAmount().toFixed(2)}</p>
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <Label className="text-sm text-blue-800">Balance Due</Label>
                <p className="text-2xl font-bold text-blue-600" data-testid="balance-due">₹{calculateBalanceDue().toFixed(2)}</p>
              </div>
            </div>

            <div className="space-y-3">
              <Label>Payment Mode *</Label>
              <RadioGroup
                value={formData.payment_mode}
                onValueChange={(value) => setFormData({ ...formData, payment_mode: value })}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="cash" id="cash" data-testid="payment-mode-cash" />
                  <Label htmlFor="cash" className="cursor-pointer">Cash</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="online" id="online" data-testid="payment-mode-online" />
                  <Label htmlFor="online" className="cursor-pointer">Online</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="both" id="both" data-testid="payment-mode-both" />
                  <Label htmlFor="both" className="cursor-pointer">Both Cash & Online</Label>
                </div>
              </RadioGroup>
            </div>

            {formData.payment_mode === 'cash' && (
              <div className="space-y-2">
                <Label htmlFor="cash_amount_single">Cash Amount Collected *</Label>
                <Input
                  id="cash_amount_single"
                  data-testid="cash-amount-single-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.cash_amount}
                  onChange={(e) => setFormData({ ...formData, cash_amount: e.target.value })}
                  placeholder="Enter cash amount collected"
                  required
                />
                <p className="text-xs text-slate-500">
                  Expected Balance Due: ₹{calculateBalanceDue().toFixed(2)}
                </p>
              </div>
            )}

            {formData.payment_mode === 'online' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="online_amount_single">Online Amount Collected *</Label>
                  <Input
                    id="online_amount_single"
                    data-testid="online-amount-single-input"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.online_amount}
                    onChange={(e) => setFormData({ ...formData, online_amount: e.target.value })}
                    placeholder="Enter online amount collected"
                    required
                  />
                  <p className="text-xs text-slate-500">
                    Expected Balance Due: ₹{calculateBalanceDue().toFixed(2)}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="scanner">Scanner *</Label>
                  <Select
                    value={formData.scanner_id}
                    onValueChange={(value) => setFormData({ ...formData, scanner_id: value })}
                  >
                    <SelectTrigger data-testid="scanner-select">
                      <SelectValue placeholder="Select scanner" />
                    </SelectTrigger>
                    <SelectContent>
                      {scanners.map(scanner => (
                        <SelectItem key={scanner.id} value={scanner.id}>{scanner.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {formData.payment_mode === 'both' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cash_amount">Cash Amount *</Label>
                  <Input
                    id="cash_amount"
                    data-testid="cash-amount-input"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.cash_amount}
                    onChange={(e) => setFormData({ ...formData, cash_amount: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="online_amount">Online Amount *</Label>
                  <Input
                    id="online_amount"
                    data-testid="online-amount-input"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.online_amount}
                    onChange={(e) => setFormData({ ...formData, online_amount: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="scanner_both">Scanner *</Label>
                  <Select
                    value={formData.scanner_id}
                    onValueChange={(value) => setFormData({ ...formData, scanner_id: value })}
                  >
                    <SelectTrigger data-testid="scanner-select-both">
                      <SelectValue placeholder="Select scanner" />
                    </SelectTrigger>
                    <SelectContent>
                      {scanners.map(scanner => (
                        <SelectItem key={scanner.id} value={scanner.id}>{scanner.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Submit Button */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/view-bills')}
          >
            Cancel
          </Button>
          <Button
            data-testid="submit-bill-btn"
            type="submit"
            disabled={loading}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                <Save size={16} />
                {isEdit ? 'Update Bill' : 'Create Bill'}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
